import { FC, useState, MouseEvent } from 'react';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  rawMaterialCategoryDialogTitles,
  rawMaterialCategoryDialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { RawMaterialCategory } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { rawMaterialCategoryInitialValues } from 'src/utils/initialValues.utils';
import { rawMaterialCategoryValidationSchema } from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';
import { Box } from '@mui/material';

const RawMaterialCategoryActionMenuButton: FC<{
  rowId: any;
  name: string;
  fetchCategories: () => void;
  deleteCategory: (id: string) => Promise<boolean>;
}> = ({ rowId, name, fetchCategories, deleteCategory }) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] =
    useState<RawMaterialCategory | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleEditFormSubmit = async () => {
    if (!selectedCategory) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(
        apiUrls.EDIT_RAW_MATERIAL_CATEGORY,
        {
          id: rowId,
          name: formik.values.name,
        }
      );
      setEditDialogOpen(false);
      fetchCategories();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (category: RawMaterialCategory) => {
    setSelectedCategory(category);

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.RAW_MATERIAL_CATEGORY_BY_ID,
        { params: { id: category.id } }
      );

      formik.setValues({
        name: response.data.name,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCategory(null);
    formik.resetForm();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId) {
          const success = await deleteCategory(rowId);
          if (success) {
            fetchCategories();
          }
        }
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
    }
  };

  const formik = useFormik({
    initialValues: rawMaterialCategoryInitialValues,
    validationSchema: rawMaterialCategoryValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: rowId,
                name: name,
                createdAt: '',
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height="340px"
            width="600px"
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={rawMaterialCategoryDialogTitles.EDIT}
            subtitle={rawMaterialCategoryDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedCategory && (
              <Box display="flex" flexDirection="column">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  gap={2}
                  mt={2}
                >
                  <TextField
                    width="150%"
                    id="name"
                    label="Category Name"
                    formikProps={formik}
                  />
                </Box>
              </Box>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        open={dialogOpen}
        paddingRight="60px"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={rawMaterialCategoryDialogTitles.DELETE}
        subtitle={rawMaterialCategoryDialogSubtitles.DELETE}
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
      />
    </>
  );
};

export default RawMaterialCategoryActionMenuButton;
